import { Loader2, CheckCircle, X, Download, AlertCircle, Info } from 'lucide-react'
import { useState, useEffect, useRef, useActionState } from 'react'
import axios from "axios";
import { useNavigate } from "react-router-dom";
import DateRangePicker from "../Daterangepicker.jsx"
import Pagination from '../Common/Pagination';
import edithistoryicon from "../../assets/edit History.svg"
import documentIcon from "../../assets/document 1.svg";
import unlinkicon from "../../assets/link 1.svg"
import EditHistoryView from './Edithistoryview.jsx';
import ErrorModal from "../ErrorModal.jsx"
import UploadBankResponseModal from '../Pages/UploadBankResponseModal.jsx'
import { usePayment } from '../../context/PaymentContext.jsx';
import Payouthistoryview from './Payouthistoryview.jsx';
import sendtobankicon from "../../assets/Send to Bank (1).svg"
import i_icon from "../../assets/i icon.jpg"
import document_icon from "../../assets/document.png"


import ConfirmModal, {
  ApproveIcon,
  SendIcon,
  FileIcon,
} from './ConfirmModal.jsx';


const BASE_URL = process.env.REACT_APP_BASE_URL;

const ChevronIcon = ({ open }) => (
  <svg className={`h-4 w-4 text-gray-400 transform transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`}
    xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

// ── Info Tooltip Component (Popover) ──────────────────────────────────────────
const InfoPopover = ({ role, featureAccess, showActions, canMerge, canApproveReject, canUploadBankResponse, canDownloadBankResponse, onClose }) => {
  return (
    <div className="absolute top-full right-0 mt-2 z-[1000] animate-in fade-in slide-in-from-top-2 duration-200">
      <div className="bg-indigo-900 text-white rounded-lg shadow-xl px-4 py-2.5 w-80 border border-indigo-700">
        <div className="flex items-start gap-3">
          <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-indigo-300" />
          <div className="flex-1">
            <p className="text-xs font-semibold mb-1">
              {role === "super_admin" ? "🔓 Super Admin View" : "👤 Admin View"}
            </p>
            <p className="text-[11px] text-indigo-200 mb-1">
              Feature Access: <span className="font-mono">{featureAccess.join(', ') || 'None'}</span>
            </p>
            <div className="text-[10px] text-indigo-200 space-y-0.5">
              {showActions && <div> • Full accounts actions visible</div>}
              {canMerge && <div> • Can merge payout files</div>}
              {canApproveReject && <div>✓✗ • Can approve/reject files</div>}
              {canUploadBankResponse && <div> • Can upload bank responses</div>}
              {canDownloadBankResponse && <div> • Can download bank responses</div>}
              {!showActions && <div>ℹ • View only mode - no actions available</div>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-indigo-300 hover:text-white transition-colors flex-shrink-0"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Simple Success Modal ──────────────────────────────────────────────────────
const SuccessModal = ({ onClose, records, amount, createdBy, filename }) => (
  <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-xl shadow-xl w-[300px] p-6 relative flex flex-col items-center">
      <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
        <X className="h-4 w-4" />
      </button>
      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
        <CheckCircle className="h-7 w-7 text-green-500" />
      </div>
      <h2 className="text-sm font-bold text-gray-800 text-center">File Created Successfully!</h2>
      {filename && (
        <div className="mt-2 w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-1.5 text-center">
          <span className="text-[10px] text-gray-400">File Name</span>
          <p className="text-xs font-semibold text-indigo-600 break-all">{filename}</p>
        </div>
      )}
      <p className="text-xs text-gray-500 mt-2 text-center leading-relaxed">
        Payout file generated for{' '}
        <span className="font-semibold text-gray-700">{records} records</span> of amount{' '}
        <span className="font-semibold text-gray-700">₹{amount}</span> by{' '}
        <span className="font-semibold text-gray-700">{createdBy}</span>.
      </p>
      <button
        onClick={onClose}
        className="mt-5 w-full py-1.5 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors font-medium"
      >
        OK
      </button>
    </div>
  </div>
);

// ── Merge Success Modal ───────────────────────────────────────────────────────
const MergeSuccessModal = ({ onClose, mergeResult }) => (
  <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-xl shadow-xl w-[320px] p-6 flex flex-col items-center gap-3">
      <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
        <CheckCircle className="h-7 w-7 text-purple-500" />
      </div>
      <h2 className="text-sm font-bold text-gray-800 text-center">Files Merged Successfully!</h2>
      <div className="w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-center">
        <span className="text-[10px] text-gray-400 block mb-1">Combined File Name</span>
        <p className="text-xs font-semibold text-purple-600 break-all">
          {mergeResult?.parent?.filename || '-'}
        </p>
      </div>
      <p className="text-xs text-gray-500 text-center leading-relaxed">
        <span className="font-semibold text-gray-700">{mergeResult?.children_ids?.length ?? 0}</span> files combined successfully.{' '}
        {/* <span className="font-semibold text-gray-700">{mergeResult?.daily_payments_reassigned ?? 0}</span> daily payment records reassigned. */}
      </p>
      <button
        onClick={onClose}
        className="mt-2 w-full py-1.5 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors font-medium"
      >
        OK
      </button>
    </div>
  </div>
);

const spinner = <><Loader2 className="h-3 w-3 animate-spin" /> Generating...</>;
const mergeSpinner = <><Loader2 className="h-3 w-3 animate-spin" /> Merging...</>;

// const STATUS_OPTIONS = [
//   "ALL", "file_updated", "submitted_to_admin", "approved_by_admin", "submitted_to_account",
//   "approved_by_accounts", "file_generated", "downloaded_by_accounts", "bank_response_uploaded",
//   "success", "discarded", "rejected_by_accounts", "rejected_by_admin", "file_updated_by_admin",
//   "file_to_be_generated", "merged_into_parent","sent_to_bank"
// ]; 

const adminaccess = ["file_updated", "file_generated","file_to_be_generated"];
const superadminaccess = ["file_updated", "file_generated", "file_to_be_generated","file_to_be_generated_by_approver", "submitted_to_admin", "rejected_by_admin", "file_updated_by_admin", "file_to_be_generated"];

const STATUS_LABELS = {
  ALL: "Select All",
  file_updated: "File Updated",
  submitted_to_admin: "Submitted to Admin",
  approved_by_admin: "Approved by Admin",
  submitted_to_account: "Submitted to Accounts",
  approved_by_accounts: "Approved by Accounts",
  file_generated: "File Generated",
  downloaded_by_accounts: "Downloaded by Accounts",
  bank_response_uploaded: "Bank Response Uploaded",
  success: "Success",
  discarded: "Discarded",
  rejected_by_accounts: "Rejected by Accounts",
  rejected_by_admin: "Rejected by Admin",
  file_updated_by_admin: "File Updated by Admin",
  file_to_be_generated: "File to be Regenerated",
  merged_into_parent: "Merged into Parent File",
sent_to_bank:"Sent to bank",
file_to_be_generated_by_approver:"File to be Regenerated by Approver"



};



const ITEMS_PER_PAGE = 4;
const MODAL_ITEMS_PER_PAGE = 6;

const Payoutlisting = () => {

  // ── Payment context ───────────────────────────────────────────────────────
  const {
    getAllPayoutFiles,
    createPayoutFile,
    submitPayoutFile,
    getPayoutFileHistory,
    getPayoutAccountHistory,
    getPayoutreferralAccountHistory,
    getPayoutincentiveAccountHistory,
    downloadPayoutCSV,
    downloadreferralPayoutCSV,
    downloadincentivePayoutCSV,
    combinePayoutFiles,
  } = usePayment();

  const [loading, setLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const navigate = useNavigate();

  // ── Success modal ─────────────────────────────────────────────────────────
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const formatDate = (date) => {
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

const [dateRange, setDateRange] = useState({
  startDate: formatDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000)),
  endDate: formatDate(new Date())
});

  // ── Merge state ───────────────────────────────────────────────────────────
  const [expandedParents, setExpandedParents] = useState(new Set());
  const [mergeLoading, setMergeLoading] = useState(false);
  const [showMergeSuccessModal, setShowMergeSuccessModal] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);

  // ── Merge Modal state ─────────────────────────────────────────────────────
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [modalFiles, setModalFiles] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalSelectedIds, setModalSelectedIds] = useState([]);
  const [modalDateRange, setModalDateRange] = useState({ startDate: "", endDate: "" });
  const [modalStatus, setModalStatus] = useState("");
  const [modalPage, setModalPage] = useState(1);
  const [modalTotalPages, setModalTotalPages] = useState(1);
  const [modalTotalItems, setModalTotalItems] = useState(0);
  const [isModalStatusOpen, setIsModalStatusOpen] = useState(false);
  const [modalfile, setmodalfile] = useState(false);

  // ── Location / Company ────────────────────────────────────────────────────
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedLocationObject, setSelectedLocationObject] = useState(null);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedCompanyName, setSelectedCompanyName] = useState("");
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [editmodalopen, seteditmodalopen] = useState(false);
  const [payouthistorymodal, setpayouthistorymodal] = useState(false);
  const [showApprovalSuccessModal, setShowApprovalSuccessModal] = useState(false);
  const [showapprovalrejectmodal, setshowapprovalrejectmodal] = useState(false);
  const [editHistoryData, setEditHistoryData] = useState([]);
  const [payoutaccountdata, setpayoutaccountdata] = useState([])
  const [editHistoryLoading, setEditHistoryLoading] = useState(false);
    const [payoutHistoryLoading, setpayoutHistoryLoading] = useState(false);
    const [isUTVMember, setisUTVMember] = useState("")

  const [selectedFileId, setSelectedFileId] = useState(null);

  const [showBankModal, setShowBankModal] = useState(false);
  const [selectedBankFileId, setSelectedBankFileId] = useState(null);

  const [selectedBankFileName, setSelectedBankFileName] = useState(null);

  const [showGenerateConfirmModal, setShowGenerateConfirmModal] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');
  const [isErrorOpen, setIsErrorOpen] = useState(false);

  // ── Info popover state ────────────────────────────────────────────────────
  const [showInfoPopover, setShowInfoPopover] = useState(false);
  const infoButtonRef = useRef(null);

  const locationRef = useRef();
  const companyRef = useRef();
  const statusRef = useRef();

  const [showRemarksModal, setShowRemarksModal] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [pendingRejectFile, setPendingRejectFile] = useState(null);

  // ── Auto-select all for Accounts view ────────────────────────────────────
  const [allLocationsSelected, setAllLocationsSelected] = useState(false);
  const [allCompaniesSelected, setAllCompaniesSelected] = useState(false);


  const [statusOptions, setStatusOptions] = useState([]);
const [statusOptionsLoading, setStatusOptionsLoading] = useState(false);



//payment_option 
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState("daily_payout");



  // State
const [roles, setRoles] = useState([]);
const [selectedRole, setSelectedRole] = useState("");
const [selectedRoleName, setSelectedRoleName] = useState("");
const [loadingRoles, setLoadingRoles] = useState(false);
const [isRoleOpen, setIsRoleOpen] = useState(false);
const roleRef = useRef(null);


const [response, setresponse] = useState([]);



  //confirmation modal
  // ── Confirmation modal state ──────────────────────────────────────────────
const [activeConfirm, setActiveConfirm] = useState(null);
const [pendingConfirmPayload, setPendingConfirmPayload] = useState(null);
const openConfirm  = (key, payload = null) => { setActiveConfirm(key); setPendingConfirmPayload(payload); };
const closeConfirm = () => { setActiveConfirm(null); setPendingConfirmPayload(null); };




 useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("superadmintoken");
        const response = await axios.get(`${BASE_URL}/lenskart/verify-token`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data?.success && response.data?.user) {
          const userData = response.data.user;
          localStorage.setItem("userData", JSON.stringify(userData));
          setresponse(response.data);
          setisUTVMember(response.data?.user?.UTVMember);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchUserData();
  }, [BASE_URL]);



  // const response = {
  //   user: {
  //     UTVMember: true,
  //     role: "admin",
  //     featureAccess: ["payout_accounts"],
  //     MyCompany: "68f1dd7a8d5bd56f07b78a5d",
  //   }
  // };


  //   const response = {
  //   user: {
  //     UTVMember: true,
  //     role: "admin",
  //     featureAccess: [
  //       "payout_generator",
  //       "payout_approver"
  //     ],
  //     MyCompany: "68f1dd7a8d5bd56f07b78a5d",
  //   }
  // };

  const ROLE_TO_FEATURE = {
  "Payout Generator": "payout_generator",
  "Payout Approver": "payout_approver",
  "Payout Accounts":"payout_accounts"
};

const PAYMENT_OPTIONS = [

  { value: "daily_payout", label: "Daily Payout" },
   { value: "incentive", label: "Incentives" },
    { value: "referral", label: "Referrals" },
];
  

  const isadmin = response?.user?.role === "Admin";
  const isSuperadmin = response?.user?.role === "super_admin";

  const hasAccountsAccess = isSuperadmin || (isadmin && response.user.featureAccess?.includes("Payout Accounts"));
  const haspayoutapprover=(isadmin && response.user.featureAccess?.includes("payout_generator") && !response.user.featureAccess?.includes("payout_approver"));

  const canMerge = hasAccountsAccess;
  const canApproveReject = hasAccountsAccess;
  const canUploadBankResponse = hasAccountsAccess;
  const canDownloadBankResponse = hasAccountsAccess;
  const showAccountsActions = hasAccountsAccess;


   const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedLabel = PAYMENT_OPTIONS.find((o) => o.value === selectedValue)?.label;










// Fetch roles when company changes
useEffect(() => {
  if (!selectedCompany || hasAccountsAccess) {
    setRoles([]);
    setSelectedRole("");
    setSelectedRoleName("");
    return;
  }

  const fetchRoles = async () => {
    setLoadingRoles(true);
    setSelectedRole("");
    setSelectedRoleName("");
    setRoles([]);

    try {
      const token = localStorage.getItem("superadmintoken");
      const res = await axios.get(
        `${BASE_URL}/payment/payout-roles/${selectedCompany}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const rolesArray = res?.data?.data?.roles || [];

      const formattedRoles = rolesArray.map((r, index) => ({
        _id: index.toString(),
        name: r,
      }));

      setRoles(formattedRoles);

      if (formattedRoles.length === 1) {
        setSelectedRole(formattedRoles[0]._id);
        setSelectedRoleName(formattedRoles[0].name);
      }

    } catch (err) {
      console.error("Failed to fetch roles", err);
    } finally {
      setLoadingRoles(false);
    }
  };

  fetchRoles();
}, [selectedCompany, hasAccountsAccess, BASE_URL]);

// Close dropdown on outside click (same pattern as company)
useEffect(() => {
  const handleClickOutside = (e) => {
    if (roleRef.current && !roleRef.current.contains(e.target)) {
      setIsRoleOpen(false);
    }
  };
  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);



useEffect(() => {
  if (!selectedRoleName && !hasAccountsAccess) {
    setStatusOptions([]);
    setSelectedStatus("");
    setIsStatusOpen(false);
    return;
  }

  const fetchStatusOptions = async () => {
    try {
      setStatusOptionsLoading(true);

      let role;
      if (hasAccountsAccess) {
        role = "accounts"; // default role for accounts access
      } else if (selectedRoleName === "Payout Approver") {
        role = "payout_approver";
      } else if (selectedRoleName === "Accounts") {
        role = "accounts";
      }

      const token = localStorage.getItem("superadmintoken");
      const res = await axios.post(
        `${BASE_URL}/payment/get-payout-statuses`,
        { role },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );

      if (res.data?.success && Array.isArray(res.data.statuses)) {
        setStatusOptions(res.data.statuses);
      }
    } catch (err) {
      console.error("Error fetching status options:", err);
    } finally {
      setStatusOptionsLoading(false);
    }
  };

  fetchStatusOptions();
}, [selectedRoleName, hasAccountsAccess, BASE_URL]);



  // ── Close popover on outside click ───────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (infoButtonRef.current && !infoButtonRef.current.contains(event.target)) {
        setShowInfoPopover(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Auto-select all locations ─────────────────────────────────────────────
  useEffect(() => {
    if (hasAccountsAccess && locations.length > 0 && !selectedLocation) {
      setAllLocationsSelected(true);
      setSelectedLocationObject(null);
    }
  }, [hasAccountsAccess, locations, selectedLocation]);

  // ── Auto-select all companies ─────────────────────────────────────────────
  useEffect(() => {
    if (hasAccountsAccess && companies.length > 0 && !selectedCompany) {
      setAllCompaniesSelected(true);
    }
  }, [hasAccountsAccess, companies, selectedCompany]);

  // ── Download Bank Response ────────────────────────────────────────────────
  const handleDownloadBankResponse = (s3Url, fileName) => {
    if (!s3Url) return;
    window.open(s3Url, '_blank');
  };

  const handleRejectClick = (fileId, fileStatus) => {
    setPendingRejectFile({ id: fileId, status: fileStatus });
    setRemarks("");
    setShowRemarksModal(true);
  };

  const handleRemarksSubmit = () => {
    if (!pendingRejectFile) return;
    setShowRemarksModal(false);
    handleSendForaccountApproval(pendingRejectFile.id, pendingRejectFile.status, false, remarks);
    setRemarks("");
    setPendingRejectFile(null);
  };

  // ── Outside click for dropdowns ───────────────────────────────────────────
  useEffect(() => {
    const handleClick = (e) => {
      if (locationRef.current && !locationRef.current.contains(e.target)) setIsLocationOpen(false);
      if (companyRef.current && !companyRef.current.contains(e.target)) setIsCompanyOpen(false);
      if (statusRef.current && !statusRef.current.contains(e.target)) setIsStatusOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, []);

  useEffect(() => {
  if (hasAccountsAccess) {
    setSelectedLocation("All Locations");
    setSelectedLocationObject({ _id: "all", name: "All Locations" });
  }
}, [hasAccountsAccess]);

  // ── Fetch locations ───────────────────────────────────────────────────────
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const token = localStorage.getItem("superadmintoken");
        const res = await axios.get(`${BASE_URL}/payment/payout-locations`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        if (res.data.success && Array.isArray(res.data.data.locations))
          setLocations(res.data.data.locations);
      } catch (err) { console.error("Error fetching locations:", err); }
    };
    fetchLocations();
  }, [BASE_URL]);


  // Auto-select All Companies for hasAccountsAccess
// Auto-select for hasAccountsAccess only
// Auto-select company based on location selection
useEffect(() => {
  if (hasAccountsAccess || selectedLocationObject?._id === "all") {
    setSelectedCompany("all");
    setSelectedCompanyName("All Companies");
  } else {
    setSelectedCompany("");
    setSelectedCompanyName("");
  }
}, [hasAccountsAccess, selectedLocationObject]);

// Fetch companies
useEffect(() => {
  const fetchCompanies = async () => {
    const token = localStorage.getItem("superadmintoken");
    const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

    // Case 1: No location selected
    if (!selectedLocationObject) { setCompanies([]); return; }

    // Case 2: "All Locations" selected — load from first location just for display
    if (selectedLocationObject._id === "all") {
      if (locations.length === 0) { setCompanies([]); return; }
      try {
        setLoadingCompanies(true);
        const res = await axios.get(`${BASE_URL}/payment/payout-companies/${locations[0]._id}`, { headers });
        setCompanies(res.data?.data?.companies && Array.isArray(res.data.data.companies) ? res.data.data.companies : []);
      } catch { setCompanies([]); }
      finally { setLoadingCompanies(false); }
      return;
    }

    // Case 3: Specific location selected — always uses the actual selected location ID
    try {
      setLoadingCompanies(true);
      const res = await axios.get(`${BASE_URL}/payment/payout-companies/${selectedLocationObject._id}`, { headers });
      setCompanies(res.data?.data?.companies && Array.isArray(res.data.data.companies) ? res.data.data.companies : []);
    } catch { setCompanies([]); }
    finally { setLoadingCompanies(false); }
  };

  fetchCompanies();
}, [selectedLocationObject, locations, BASE_URL]);

  useEffect(() => {
    if (!hasAccountsAccess) {
      setSelectedCompany("");
      setSelectedCompanyName("");
    }
  }, [selectedLocation, hasAccountsAccess]);

  // ── Date / Status ─────────────────────────────────────────────────────────
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isStatusOpen, setIsStatusOpen] = useState(false);

  // ── Pagination ────────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // ── Data ──────────────────────────────────────────────────────────────────
  const [listingData, setListingData] = useState([]);
  const [summaryData, setSummaryData] = useState({ totalFiles: 0, totalPaidAmount: 0, totalRecords: 0 });
  const [showTable, setShowTable] = useState(false);
  const [payoutDetails, setPayoutDetails] = useState({ records: 0, amount: 0, createdBy: '', filename: '' });

  // ── Auth / Init ───────────────────────────────────────────────────────────
  useEffect(() => {
    const initializeApp = async () => {
      try {
        setIsInitializing(true);
        const token = localStorage.getItem("superadmintoken");
        if (!token) { setIsInitializing(false); return; }
        const res = await axios.get(`${BASE_URL}/lenskart/verify-token`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.error?.name === "TokenExpiredError") {
          localStorage.removeItem("superadmintoken");
          navigate("/lkart/superadmin/");
          return;
        }
      } catch {
      } finally { setIsInitializing(false); }
    };
    initializeApp();
  }, [navigate, BASE_URL]);

  // ── Fetch listing data ────────────────────────────────────────────────────
  const fetchListingData = async (page = currentPage) => {
    try {

            let role;
      if (hasAccountsAccess) {
        role = "accounts"; // default role for accounts access
      } else if (selectedRoleName === "Payout Approver") {
        role = "payout_approver";
      } else if (selectedRoleName === "Accounts") {
        role = "accounts";
      }
      setLoading(true);
      const companyIdToSend = (hasAccountsAccess && allCompaniesSelected) ? "" : selectedCompany;
const data = await getAllPayoutFiles({
  company_id: companyIdToSend === "all" ? "" : companyIdToSend,
  startDate: dateRange.startDate,
  endDate: dateRange.endDate,
  page,
  limit: ITEMS_PER_PAGE,
  file_status: selectedStatus,
  payout_type: selectedValue,
  role
});
      if (data?.success) {
        setListingData(data.data ?? []);
        setSummaryData({
          totalFiles: data.summary.totalFiles ?? 0,
          totalPayableAmount: data.summary.totalDpAmount===0?data.summary.totalNetAmount:data.summary.totalDpAmount,
          totalPaidAmount: data.summary.totalDpSuccessAmount ?? 0,

          totalRecords: data.summary.totalDpRecords===0?data.summary.totalRecords:data.summary.totalDpRecords,
        });
        setTotalPages(data.pagination?.totalPages ?? 1);
        setTotalItems(data.pagination?.totalItems ?? 0);
        setCurrentPage(page);
        setShowTable(true);
      }
    } catch (err) {
      console.error("Error fetching listing data:", err);
      setListingData([]);
      setSummaryData({ totalFiles: 0, totalPaidAmount: 0, totalRecords: 0 });
    } finally { setLoading(false); }
  };

  // ── Fetch modal files ─────────────────────────────────────────────────────
const fetchModalFiles = async (page = 1) => {
  try {
    setModalLoading(true);
    const data = await getAllPayoutFiles({
      company_id: "",
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      page,
      limit: MODAL_ITEMS_PER_PAGE,
      file_status: "approved_by_accounts",
      payout_type: selectedValue,
    });
    if (data?.success) {
      const files = (data.data ?? []).filter(
        f =>
          !superadminaccess.includes(f.fileStatus) &&
          f.parentId === null &&
          f.ismerged === false
      );
      setModalFiles(files);
      setModalTotalPages(data.pagination?.totalPages ?? 1);
      setModalTotalItems(data.pagination?.totalItems ?? 0);
      setModalPage(page);
      setmodalfile(true);
    }
  } catch (err) {
    console.error("Error fetching modal files:", err);
    setModalFiles([]);
  } finally {
    setModalLoading(false);
  }
};

  // ── Submit / approve / reject ─────────────────────────────────────────────
  const handleSendForaccountApproval = async (payoutfilesid, file_status, accepted, remarks = "") => {
    try {
      setLoading(true);
      const data = await submitPayoutFile({
        payoutfiles_id: payoutfilesid,
        file_status,
        ...(accepted === false && file_status === "rejected_by_admin" && { rejection_reason_by_admin: remarks }),
        ...(accepted === false && file_status === "rejected_by_accounts" && { rejection_reason_by_accounts: remarks }),
      });
      if (data?.success) {
        if (accepted) {
          setShowApprovalSuccessModal(true);
        } else {
          setshowapprovalrejectmodal(true);
        }
        fetchListingData(currentPage);
      }
    } catch (error) {
      console.error("Error submitting payout file:", error);
    } finally {
      setLoading(false);
    }
  };

  // ── Expand/collapse parent rows ───────────────────────────────────────────
  const toggleParentExpand = (parentId) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      next.has(parentId) ? next.delete(parentId) : next.add(parentId);
      return next;
    });
  };

  // ── Modal merge handler ───────────────────────────────────────────────────
  const handleModalMerge = async () => {
    if (modalSelectedIds.length < 2) return;
    try {
      setMergeLoading(true);
      const selectedFiles = modalFiles.filter(f => modalSelectedIds.includes(f.id));
      const fileStatus = selectedFiles[0]?.fileStatus;
      const data = await combinePayoutFiles({
        payoutfiles_ids: modalSelectedIds,
        file_status: fileStatus,
      });
      if (data?.success) {
        setMergeResult(data.data);
        setShowMergeModal(false);
        setShowMergeSuccessModal(true);
        setModalSelectedIds([]);
        fetchListingData(currentPage);
      }
    } catch (err) {
      console.error("Error merging payout files:", err);
      const reason =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Merge failed. Please try again.";
      setErrorMessage(reason);
      setIsErrorOpen(true);
    } finally {
      setMergeLoading(false);
    }
  };

  // ── Apply button ──────────────────────────────────────────────────────────
  const handleApply = () => {
    setCurrentPage(1);

      const selectedFeature = ROLE_TO_FEATURE[selectedRoleName];
if (!hasAccountsAccess) {
  setresponse((prev) => {
    if (!prev?.user) return prev;

    return {
      ...prev,
      user: {
        ...prev.user,
        featureAccess: selectedFeature ? [selectedFeature] : [],
      },
    };
  });
}

  console.log(response.user.featureAccess)

  
    fetchListingData(1);
    setmodalfile(false);

  };

  // ── Pagination change ─────────────────────────────────────────────────────
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchListingData(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Generate Payout File ──────────────────────────────────────────────────
  const handleGeneratePayoutFile = async () => {
    try {
      setGenerateLoading(true);
      const companyIdToSend = (hasAccountsAccess && allCompaniesSelected) ? "" : selectedCompany;
      const data = await createPayoutFile({
        company_id: companyIdToSend,
        file_status: "file_generated"
      });
      if (data?.success) {
        setPayoutDetails({
          records: data.summary?.totalEntries ?? 0,
          amount: data.summary?.totalAmount ?? 0,
          createdBy: data?.performedBy ?? '-',
          filename: data.payoutFile?.filename ?? '',
        });
        setShowSuccessModal(true);
        fetchListingData(currentPage);
      }
    } catch (err) {
      console.error("Error generating payout file:", err);
      const reason = err.response?.data?.message
        || err.response?.data?.error
        || err.message
        || "Something went wrong. Please try again.";
      setErrorMessage(reason);
      setIsErrorOpen(true);
    } finally {
      setGenerateLoading(false);
    }
  };

  // ── Generate Button ───────────────────────────────────────────────────────
  const GenerateButton = () => {
    const btnClass = "px-4 h-7 bg-[#4F46E5] text-white text-xs rounded-md hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed flex items-center gap-1.5 whitespace-nowrap font-medium shadow-sm transition-colors";

    if (isadmin && response.user.featureAccess?.includes("payout_generator") && !hasAccountsAccess) {
      return (
        <button
          onClick={() => openConfirm('generatePayout')}
          disabled={generateLoading || (!selectedCompany && !allCompaniesSelected)}
          className={btnClass}
        >
          {generateLoading ? spinner : "+ Generate Payout File"}
        </button>
      );
    }

    if (hasAccountsAccess) {
      return (
        <button
          onClick={() => {
            setShowMergeModal(true);
            setModalSelectedIds([]);
            setModalDateRange({ startDate: "", endDate: "" });
            setModalStatus("");
            setModalFiles([]);
            setModalPage(1);
            setModalTotalPages(1);
            fetchModalFiles();
          }}
          disabled={generateLoading}
          className={btnClass}
        >
          {generateLoading ? spinner : "+ Create Merge Payout File"}
        </button>
      );
    }

    return null;
  };

  // ── Edit history ──────────────────────────────────────────────────────────
  const handleViewEditHistory = async (fileId) => {
    setSelectedFileId(fileId);
    seteditmodalopen(true);
    try {
      setEditHistoryLoading(true);
      const data = await getPayoutFileHistory(fileId);
      if (data?.success) {
        setEditHistoryData(data.data ?? []);
      }
    } catch (err) {
      console.error("Error fetching edit history:", err);
      setEditHistoryData([]);
    } finally {
      setEditHistoryLoading(false);
    }
  };

    const handleViewPayoutHistory = async (fileId) => {
    setSelectedFileId(fileId);
    setpayouthistorymodal(true);
    try {
      setpayoutHistoryLoading(true);
      const data = await getPayoutAccountHistory(fileId);
      if (data?.success) {
        setpayoutaccountdata(data.data ?? []);
      }
    } catch (err) {
      console.error("Error fetching edit history:", err);
      setpayoutaccountdata([]);
    } finally {
      setpayoutHistoryLoading(false);
    }
  };

      const handleViewreferralPayoutHistory = async (fileId) => {
    setSelectedFileId(fileId);
    setpayouthistorymodal(true);
    try {
      setpayoutHistoryLoading(true);
      const data = await getPayoutreferralAccountHistory(fileId);
      if (data?.success) {
        setpayoutaccountdata(data.data ?? []);
      }
    } catch (err) {
      console.error("Error fetching edit history:", err);
      setpayoutaccountdata([]);
    } finally {
      setpayoutHistoryLoading(false);
    }
  };


        const handleViewincentivePayoutHistory = async (fileId) => {
    setSelectedFileId(fileId);
    setpayouthistorymodal(true);
    try {
      setpayoutHistoryLoading(true);
      const data = await getPayoutincentiveAccountHistory(fileId);
      if (data?.success) {
        setpayoutaccountdata(data.data ?? []);
      }
    } catch (err) {
      console.error("Error fetching edit history:", err);
      setpayoutaccountdata([]);
    } finally {
      setpayoutHistoryLoading(false);
    }
  };

  //   const handleViewreferralPayoutHistory = async (fileId) => {
  //   setSelectedFileId(fileId);
  //   setpayouthistorymodal(true);
  //   try {
  //     setpayoutHistoryLoading(true);
  //     const data = await getPayoutAccountHistory(fileId);
  //     if (data?.success) {
  //       setpayoutaccountdata(data.data ?? []);
  //     }
  //   } catch (err) {
  //     console.error("Error fetching edit history:", err);
  //     setpayoutaccountdata([]);
  //   } finally {
  //     setpayoutHistoryLoading(false);
  //   }
  // };



const handleFileClick = (fileId,payoutType,fileName,companyId) => {
  if (!hasAccountsAccess) {

    if(payoutType=="daily_payout")
    {
    window.open(
      `/lkart/superadmin/payout-file?id=${fileId}&role=${encodeURIComponent(selectedRoleName)}&companyId=${companyId}`,
      "_blank"
    );
  }
  else
  {
    window.open(
      `/lkart/superadmin/referral-payout-file?id=${fileId}&payout_type=${payoutType}&companyId=${companyId}`,
      "_blank"
    );

  }


  } else {
    if(payoutType=="daily_payout")
    {
    handleViewPayoutHistory(fileId);
    }
    else if(payoutType=="incentive")
    {
      handleViewincentivePayoutHistory(fileId);


    }
    else{
    handleViewreferralPayoutHistory(fileId);


    }
    

  }
};
  

  // ── Download payout CSV ───────────────────────────────────────────────────
const downloadbankresponse = async (fileId, fileName) => {
  try {
    setLoading(true);

    // ✅ downloadPayoutCSV already handles the download internally
    await downloadPayoutCSV(fileId,fileName, "downloaded_by_accounts");

    fetchListingData(currentPage);

  } catch (error) {
    console.error("Error downloading payout CSV:", error);
    setErrorMessage(
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Download failed. Please try again."
    );
    setIsErrorOpen(true);
  } finally {
    setLoading(false);
  }
};


const downloadreferralbankresponse = async (fileId, fileName) => {
  try {
    setLoading(true);

    // ✅ downloadreferralPayoutCSV already handles the download internally
    await downloadreferralPayoutCSV(fileId, fileName, "downloaded_by_accounts");

    fetchListingData(currentPage);

  } catch (error) {
    console.error("Error downloading payout CSV:", error);
    setErrorMessage(
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Download failed. Please try again."
    );
    setIsErrorOpen(true);
  } finally {
    setLoading(false);
  }
};

const downloadincentivebankresponse = async (fileId, fileName) => {
  try {
    setLoading(true);

    // ✅ downloadincentivePayoutCSV already handles the download internally
    await downloadincentivePayoutCSV(fileId, fileName, "downloaded_by_accounts");

    fetchListingData(currentPage);

  } catch (error) {
    console.error("Error downloading payout CSV:", error);
    setErrorMessage(
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Download failed. Please try again."
    );
    setIsErrorOpen(true);
  } finally {
    setLoading(false);
  }
};


  const handleUnlinkPayoutFile = async (fileId) => {
  try {
    setLoading(true);
    const token = localStorage.getItem("superadmintoken");
    const res = await axios.post(
      `${BASE_URL}/payment/unmerge-payout-file`,
      { payoutfiles_id: fileId },
      { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
    );
    if (res.data?.success) {
      fetchListingData(currentPage);
    } else {
      setErrorMessage(res.data?.message || "Unlink failed. Please try again.");
      setIsErrorOpen(true);
    }
  } catch (err) {
    const reason =
      err.response?.data?.message ||
      err.response?.data?.error ||
      err.message ||
      "Unlink failed. Please try again.";
    setErrorMessage(reason);
    setIsErrorOpen(true);
  } finally {
    setLoading(false);
  }
};

  // ── Init loading screen ───────────────────────────────────────────────────
  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="flex flex-col items-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mb-2" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
    {isUTVMember &&
      <div className="p-4 min-h-screen flex flex-col bg-gray-50 pt-16">

        {/* Header */}
        <div className="flex items-center justify-center relative mb-3 pt-6 pb-1">
          <h1 className="font-bold text-xl text-center">Payout Listing</h1>
          {/* <div className="absolute right-0" ref={infoButtonRef}>
            <button
              onClick={() => setShowInfoPopover(!showInfoPopover)}
              className="p-1.5 rounded-full hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
              title="View permission details"
            >
              <Info className="h-5 w-5 text-gray-500 hover:text-indigo-600 transition-colors" />
            </button>
            {showInfoPopover && (
              <InfoPopover
                role={response?.user?.role}
                featureAccess={response?.user?.featureAccess || []}
                showActions={showAccountsActions}
                canMerge={canMerge}
                canApproveReject={canApproveReject}
                canUploadBankResponse={canUploadBankResponse}
                canDownloadBankResponse={canDownloadBankResponse}
                onClose={() => setShowInfoPopover(false)}
              />
            )}
          </div> */}
        </div>

        {/* ── Filter Bar ── */}
<div className="mb-3 bg-white rounded-lg shadow-sm p-3 flex-shrink-0">
  <div className="flex items-end gap-3">

    {/* Payment Options */}
    <div className="flex-1 min-w-[90px] relative" ref={ref}>
      <label className="text-[10px] text-gray-500 mb-0.5 block">PayoutFile Type</label>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-2 h-7 text-sm border border-gray-300 rounded text-left flex justify-between items-center bg-white hover:border-indigo-400 transition-colors"
      >
        <span className="text-gray-800 truncate">{selectedLabel}</span>
        <ChevronIcon open={isOpen} />
      </button>
      {isOpen && (
        <div className="absolute z-[99999] mt-1 w-full bg-white shadow-lg rounded py-0.5 max-h-40 overflow-auto border border-gray-300">
          {PAYMENT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => {
                setSelectedValue(opt.value);
                setIsOpen(false);

                if (opt.value !== "daily_payout") {
                  setSelectedLocation("");
                  setSelectedLocationObject(null);
                  setSelectedCompany("");
                  setSelectedCompanyName("");
                  setSelectedRole("");
                  setSelectedRoleName("");
                  setSelectedStatus("");
                }

                // Always reset date to last 7 days on any payment type switch
                setDateRange({
                  startDate: formatDate(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000)),
                  endDate: formatDate(new Date()),
                });
              }}
              className={`block w-full text-left px-3 py-1 text-sm hover:bg-indigo-100 ${
                selectedValue === opt.value ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>

    {/* ✅ helper — true when referral or incentive is selected */}
    {/* const isReadOnly = selectedValue === "referral" || selectedValue === "incentive" */}

    {/* Location */}
    <div className="flex-1 min-w-[70px] relative" ref={locationRef}>
      <label className="text-[10px] text-gray-500 mb-0.5 block">Location</label>
      <button
        onClick={() => {
          if (selectedValue === "daily_payout") setIsLocationOpen(!isLocationOpen);
        }}
        disabled={selectedValue === "referral" || selectedValue === "incentive"}
        className={`w-full px-2 h-7 text-sm border border-gray-300 rounded text-left flex justify-between items-center transition-colors
          ${selectedValue === "referral" || selectedValue === "incentive"
            ? "bg-gray-100 cursor-not-allowed text-gray-400"
            : "bg-white hover:border-indigo-400"
          }`}
      >
        <span className={`truncate flex-1 ${selectedLocation ? "text-gray-800" : "text-gray-400"}`}>
          {selectedLocation || "Select"}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {hasAccountsAccess && selectedLocation === "All Locations" && (
            <span className="text-[10px] text-gray-400">(auto-selected)</span>
          )}
          <ChevronIcon open={isLocationOpen} />
        </span>
      </button>

      {isLocationOpen && selectedValue === "daily_payout" && (
        <div className="absolute z-[100] mt-1 w-full bg-white shadow-lg rounded py-0.5 max-h-40 overflow-auto border border-gray-300">
          {hasAccountsAccess && (
            <button
              onClick={() => {
                setSelectedLocation("All Locations");
                setSelectedLocationObject({ _id: "all", name: "All Locations" });
                setIsLocationOpen(false);
              }}
              className={`block w-full text-left px-3 py-1 text-sm hover:bg-indigo-100 font-medium
                ${selectedLocation === "All Locations" ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}
            >
              All Locations
              <span className="ml-1 text-[10px] text-gray-400">(auto-selected)</span>
            </button>
          )}
          {hasAccountsAccess && locations.length > 0 && (
            <div className="border-t border-gray-100 my-0.5" />
          )}
          {locations.length > 0
            ? locations.map((loc) => (
                <button
                  key={loc._id}
                  onClick={() => {
                    setSelectedLocation(loc.name);
                    setSelectedLocationObject(loc);
                    setIsLocationOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-1 text-sm hover:bg-indigo-100 ${
                    selectedLocation === loc.name ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                  }`}
                >
                  {loc.name}
                </button>
              ))
            : <div className="px-3 py-1 text-sm text-gray-500">No locations found</div>
          }
        </div>
      )}
    </div>

    {/* Company */}
    <div className="flex-1 min-w-[90px] relative" ref={companyRef}>
      <label className="text-[10px] text-gray-500 mb-0.5 block">Company</label>
      <button
        onClick={() => {
          if (selectedValue !== "daily_payout") return;
          if (selectedLocationObject?._id === "all") return;
          if ((selectedLocation || hasAccountsAccess) && !loadingCompanies) setIsCompanyOpen(!isCompanyOpen);
        }}
        disabled={
          selectedValue === "referral" || selectedValue === "incentive"
            ? true
            : (!selectedLocation && !hasAccountsAccess) || loadingCompanies
        }
        className={`w-full px-2 h-7 text-sm border border-gray-300 rounded text-left flex justify-between items-center transition-colors
          ${selectedValue === "referral" || selectedValue === "incentive"
            ? "bg-gray-100 cursor-not-allowed text-gray-400"
            : (!selectedLocation && !hasAccountsAccess) || loadingCompanies
              ? "bg-gray-100 cursor-not-allowed text-gray-400"
              : selectedLocationObject?._id === "all"
                ? "bg-gray-50 cursor-default text-gray-600 border-gray-200"
                : "bg-white hover:border-indigo-400"
          }`}
      >
        {loadingCompanies
          ? <span className="flex items-center text-gray-400"><Loader2 className="h-2.5 w-2.5 animate-spin mr-1" />Loading...</span>
          : <span className={`truncate flex-1 ${selectedCompany ? "text-gray-800" : "text-gray-400"}`}>
              {selectedCompany
                ? selectedCompany === "all"
                  ? "All Companies"
                  : (() => {
                      const c = companies.find((c) => c.companyId === selectedCompany);
                      return c ? `${c.companyName}${c.companyCode ? ` (${c.companyCode})` : ""}` : "Select";
                    })()
                : "Select"}
            </span>
        }
        <span className="flex items-center gap-1 shrink-0">
          {selectedCompany === "all" && (
            <span className="text-[10px] text-gray-400">(auto-selected)</span>
          )}
          {selectedLocationObject?._id !== "all" && <ChevronIcon open={isCompanyOpen} />}
        </span>
      </button>

      {isCompanyOpen && selectedValue === "daily_payout" && selectedLocationObject?._id !== "all" && (selectedLocation || hasAccountsAccess) && !loadingCompanies && (
        <div className="absolute z-[100] mt-1 w-full bg-white shadow-lg rounded py-0.5 max-h-40 overflow-auto border border-gray-300">
          {hasAccountsAccess && (
            <>
              <button
                onClick={() => {
                  setSelectedCompany("all");
                  setSelectedCompanyName("All Companies");
                  setIsCompanyOpen(false);
                }}
                className={`block w-full text-left px-3 py-1 text-sm hover:bg-indigo-100 font-medium
                  ${selectedCompany === "all" ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}
              >
                All Companies
                <span className="ml-1 text-[10px] text-gray-400">(auto-selected)</span>
              </button>
              {companies.length > 0 && <div className="border-t border-gray-100 my-0.5" />}
            </>
          )}
          {companies.length > 0
            ? companies.map((company) => (
                <button
                  key={company.companyId}
                  onClick={() => {
                    setSelectedCompany(company.companyId);
                    setSelectedCompanyName(company.companyName);
                    setIsCompanyOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-1 text-sm hover:bg-indigo-100 ${
                    selectedCompany === company.companyId ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                  }`}
                >
                  {company.companyName}{company.companyCode ? ` (${company.companyCode})` : ""}
                </button>
              ))
            : <div className="px-3 py-1 text-sm text-gray-500">No companies found</div>
          }
        </div>
      )}
    </div>

    {/* Role */}
    {!hasAccountsAccess && (
      <div className="flex-1 min-w-[90px] relative" ref={roleRef}>
        <label className="text-[10px] text-gray-500 mb-0.5 block">Role</label>
        <button
          onClick={() => {
            if (selectedValue !== "daily_payout") return;
            if (selectedCompany && !loadingRoles && roles.length > 1) setIsRoleOpen(!isRoleOpen);
          }}
          disabled={
            selectedValue === "referral" || selectedValue === "incentive"
              ? true
              : !selectedCompany || loadingRoles
          }
          className={`w-full px-2 h-7 text-sm border border-gray-300 rounded text-left flex justify-between items-center transition-colors
            ${selectedValue === "referral" || selectedValue === "incentive"
              ? "bg-gray-100 cursor-not-allowed text-gray-400"
              : !selectedCompany || loadingRoles
                ? "bg-gray-100 cursor-not-allowed text-gray-400"
                : roles.length === 1
                  ? "bg-gray-50 cursor-default text-gray-600 border-gray-200"
                  : "bg-white hover:border-indigo-400"
            }`}
        >
          {loadingRoles ? (
            <span className="flex items-center text-gray-400">
              <Loader2 className="h-2.5 w-2.5 animate-spin mr-1" />
              Loading...
            </span>
          ) : (
            <span className={selectedRole ? "text-gray-800 truncate" : "text-gray-400"}>
              {selectedRole
                ? (() => {
                    const r = roles.find((r) => r._id === selectedRole);
                    return r ? r.name : "Select";
                  })()
                : "Select"}
            </span>
          )}
          {roles.length !== 1 && <ChevronIcon open={isRoleOpen} />}
          {roles.length === 1 && selectedRole && <span></span>}
        </button>

        {isRoleOpen && selectedValue === "daily_payout" && selectedCompany && !loadingRoles && roles.length > 1 && (
          <div className="absolute z-[100] mt-1 w-full bg-white shadow-lg rounded py-0.5 max-h-40 overflow-auto border border-gray-300">
            {roles.map((role) => (
              <button
                key={role._id}
                onClick={() => {
                  setSelectedRole(role._id);
                  setSelectedRoleName(role.name);
                  setIsRoleOpen(false);
                }}
                className={`block w-full text-left px-3 py-1 text-sm hover:bg-indigo-100 ${
                  selectedRole === role._id ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                }`}
              >
                {role.name}
              </button>
            ))}
          </div>
        )}
      </div>
    )}

    {/* Status */}
    <div className="flex-1 min-w-[90px] relative" ref={statusRef}>
      <label className="text-[10px] text-gray-500 mb-0.5 block">Status</label>
      <button
        onClick={() => {
          if (selectedValue === "daily_payout") setIsStatusOpen(!isStatusOpen);
        }}
        disabled={
          selectedValue === "referral" || selectedValue === "incentive"
            ? true
            : (!selectedRoleName && !hasAccountsAccess) || statusOptionsLoading
        }
        className={`w-full px-2 h-7 text-sm border border-gray-300 rounded text-left flex justify-between items-center transition-colors
          ${selectedValue === "referral" || selectedValue === "incentive"
            ? "bg-gray-100 cursor-not-allowed text-gray-400"
            : "bg-white hover:border-indigo-400 disabled:bg-gray-50 disabled:cursor-not-allowed"
          }`}
      >
        {statusOptionsLoading ? (
          <span className="flex items-center text-gray-400">
            <Loader2 className="h-2.5 w-2.5 animate-spin mr-1" />Loading...
          </span>
        ) : (
          <span className={selectedStatus ? "text-gray-800 truncate" : "text-gray-400"}>
            {selectedStatus ? (STATUS_LABELS[selectedStatus] ?? selectedStatus) : "Select All"}
          </span>
        )}
        <ChevronIcon open={isStatusOpen} />
      </button>

      {isStatusOpen && selectedValue === "daily_payout" && !statusOptionsLoading && (
        <div className="absolute z-[99999] mt-1 w-full bg-white shadow-lg rounded py-0.5 max-h-40 overflow-auto border border-gray-300">
          <button
            onClick={() => { setSelectedStatus(""); setIsStatusOpen(false); }}
            className={`block w-full text-left px-3 py-1 text-sm hover:bg-indigo-100 ${!selectedStatus ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}
          >
            Select All
          </button>
          {statusOptions.length > 0
            ? statusOptions.map((s) => (
                <button
                  key={s}
                  onClick={() => { setSelectedStatus(s); setIsStatusOpen(false); }}
                  className={`block w-full text-left px-3 py-1 text-sm hover:bg-indigo-100 ${
                    selectedStatus === s ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                  }`}
                >
                  {STATUS_LABELS[s] ?? s}
                </button>
              ))
            : <div className="px-3 py-1 text-sm text-gray-500">No statuses found</div>
          }
        </div>
      )}
    </div>

    {/* Date Range — always visible, disabled for referral/incentive */}
    <div className="flex-1 min-w-[130px]">
      <label className="text-[10px] text-gray-500 mb-0.5 block">Date Range</label>
      <DateRangePicker
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
        onChange={(range) => setDateRange(range)}
        placeholder="Select"
        // disabled={
        //   selectedValue === "referral" || selectedValue === "incentive"
        //     ? true
        //     : !hasAccountsAccess && !selectedCompany
        // }
        height="28px"
      />
    </div>

    {/* Apply — always visible */}
    <div className="flex-shrink-0">
      <button
        onClick={handleApply}
        disabled={
          selectedValue === "daily_payout"
            ? hasAccountsAccess
                ? (!dateRange.startDate || !dateRange.endDate || loading)
                : (!selectedCompany || !dateRange.startDate || !dateRange.endDate || loading)
            : (!dateRange.startDate || !dateRange.endDate || loading)
        }
        className="px-3 h-7 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
      >
        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Apply"}
      </button>
    </div>

  </div>
</div>

        {/* ── Payout Summary ── */}
        <div className="mb-3 bg-[#F3F9F9] rounded-lg shadow-sm p-3 flex-shrink-0">
          <h2 className="font-bold text-s text-black mb-2">Payout Summary</h2>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-700 flex-shrink-0" />
              <span className="text-gray-600 text-xs">Total Files :</span>
              <span className="font-semibold text-gray-900 text-xs">{showTable ? summaryData.totalFiles.toLocaleString() : "-"}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-pink-600 flex-shrink-0" />
              <span className="text-gray-600 text-xs">Total Payable Amount :</span>
              <span className="font-semibold text-gray-900 text-xs">{showTable ? `₹ ${summaryData.totalPayableAmount.toLocaleString()}` : "-"}</span>
            </div>
                        <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-600 flex-shrink-0" />
              <span className="text-gray-600 text-xs">Total Paid Amount :</span>
              <span className="font-semibold text-gray-900 text-xs">{showTable ? `₹ ${summaryData.totalPaidAmount.toLocaleString()}` : "-"}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-600 flex-shrink-0" />
              <span className="text-gray-600 text-xs">Total Records :</span>
              <span className="font-semibold text-gray-900 text-xs">{showTable ? summaryData.totalRecords.toLocaleString() : "-"}</span>
            </div>
          </div>
        </div>

        {/* ── Table + Pagination ── */}
        <div className="flex flex-col">

          {showTable && (
            <div className="flex-shrink-0 bg-white border-t border-gray-200 flex p-2 items-center justify-end pr-4 gap-2">
              <GenerateButton />
            </div>
          )}

          {/* ── Table ── */}
          <div className="overflow-x-auto relative">
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 bg-white shadow-md z-[50]">
                <tr className="bg-gray-100">
                  <th className="p-2 text-center text-xs font-semibold text-gray-700 w-8"></th>
                  <th className="p-2 text-left text-xs font-semibold text-gray-700">File Name</th>
                  <th className="p-2 text-center text-xs font-semibold text-gray-700">File Type</th>
                   <th className="p-2 text-center text-xs font-semibold text-gray-700">Company Name</th>
                  <th className="p-2 text-center text-xs font-semibold text-gray-700">Generated By</th>
                  <th className="p-2 text-center text-xs font-semibold text-gray-700">Total Records</th>
                  <th className="p-2 text-center text-xs font-semibold text-gray-700">Total Amt</th>
                  <th className="p-2 text-center text-xs font-semibold text-gray-700">Status</th>
                  <th className="p-2 text-center text-xs font-semibold text-gray-700">Edit History</th>
                  <th className="p-2 text-center text-xs font-semibold text-gray-700"></th>
                  <th className="p-2 text-center text-xs font-semibold text-gray-700"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="11" className="text-center p-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-600" /></td></tr>
                ) : !showTable ? (
                  <tr><td colSpan="11" className="text-center p-8 text-gray-400 text-sm">Select filters and click Apply to view data</td></tr>
                ) : listingData.length === 0 ? (
                  <tr><td colSpan="11" className="text-center p-8 text-gray-400 text-sm">No records found</td></tr>
                ) : (
                  (() => {
                    const filtered = listingData.filter((file) => {
                      if (isadmin && response.user.featureAccess?.includes("payout_approver") && !response.user.featureAccess?.includes("payout_generator")) {
                        return !adminaccess.includes(file.fileStatus);
                      }
                      if (isSuperadmin || response.user.featureAccess?.includes("payout_accounts")) {
                        return !superadminaccess.includes(file.fileStatus);
                      }
                      return true;
                    });

                    const isAccountsOrSuperadmin = hasAccountsAccess || isSuperadmin;
                    const childrenByParent = isAccountsOrSuperadmin
                      ? filtered.reduce((acc, f) => {
                        if (f.parentId) {
                          if (!acc[f.parentId]) acc[f.parentId] = [];
                          acc[f.parentId].push(f);
                        }
                        return acc;
                      }, {})
                      : {};

                    const topLevel = isAccountsOrSuperadmin
                      ? filtered.filter(f => !f.parentId || !filtered.some(p => p.id === f.parentId))
                      : filtered;
const renderFileNameCell = (file, isChild = false, hasChildren = false, isExpanded = false, onToggle = null) => (
  <td className={`p-2 text-left ${isChild ? "pl-16" : "min-w-[200px]"}`}>
    <div className="flex items-center gap-1">
      {/* Arrow toggle inside file name cell */}
      {hasChildren && (
        <button
          onClick={onToggle}
          className="inline-flex items-center justify-center w-5 h-5 rounded hover:bg-cyan-100 transition-colors flex-shrink-0"
        >
          <svg
            className={`h-3.5 w-3.5 text-cyan-600 transition-transform duration-200 ${isExpanded ? "rotate-90" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
<div
  onClick={() => handleFileClick(file.id,file.payoutType,file.fileName,file.companyId)}
  className="cursor-pointer"
>
  <div className="font-medium text-gray-800 text-xs leading-tight truncate max-w-[220px]" title={file.fileName || '-'}>
    {file.fileName || '-'}
  </div>
  <div className="text-gray-400 text-[10px] mt-0.5">
    {file.createdAt ? new Date(file.createdAt).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    }) : '-'}
  </div>
</div>

{hasAccountsAccess && (
  <div
    onClick={(e) => {
      e.stopPropagation();

      if(file.payoutType=="daily_payout")
      {
      window.open(
        `/lkart/superadmin/payout-file?id=${file.id}&role="Payout Accounts"&`,
        "_blank"
      );
      }
      else
      {
    window.open(
      `/lkart/superadmin/referral-payout-file?id=${file.id}&payout_type=${file.payoutType}`,
      "_blank"
    );
  }
    }}
    className="ml-2 flex items-center relative group"
  >
    <img
      src={document_icon}
      alt="info"
      className="w-4 h-4 cursor-pointer hover:opacity-80"
    />

    {/* Tooltip */}
    <span className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 
      whitespace-nowrap text-[10px] px-2 py-1 rounded 
      bg-gray-800 text-white opacity-0 group-hover:opacity-100 
      transition-opacity pointer-events-none z-50"
    >
      View payout listing
    </span>
  </div>
)}
    </div>
  </td>
);

                    const renderActionsCell = (file, hasChildren = false) => (
  <td className="p-2 text-right">
    <div className="flex items-center justify-end gap-2">
      {showAccountsActions && (
        <>
          {file.fileStatus === "submitted_to_account" && canApproveReject && (
            <>
              <button 
                onClick={() => handleRejectClick(file.id, "rejected_by_accounts")} 
                disabled={loading}
                className="w-[160px] py-1 text-[11px] font-semibold text-red-600 border border-red-400 rounded hover:bg-red-50 disabled:opacity-50"
              >
                Reject
              </button>
              <button 
                onClick={() => openConfirm('approve', { id: file.id, status: "approved_by_accounts" })} 
                disabled={loading}
                className="w-[160px] py-1 text-[11px] font-semibold text-white bg-green-500 rounded hover:bg-green-600 disabled:opacity-50"
              >
                Approve
              </button>
            </>
          )}

          {(file.fileStatus === "downloaded_by_accounts" || file.fileStatus === "sent_to_bank" || file.fileStatus === "approved_by_accounts") && (
            <>
              {file.fileStatus === "downloaded_by_accounts" && canUploadBankResponse && (
                <button 
                  onClick={() => { setSelectedBankFileId(file.id); setSelectedBankFileName(file.fileName); setShowBankModal(true); }} 
                  disabled={loading}
                  className="w-[160px] flex items-center justify-center gap-1.5 py-1 text-[11px] font-semibold text-blue-700 bg-transparent border border-dashed border-purple-300 rounded hover:bg-purple-50 disabled:opacity-50 transition-colors"
                >
                  <svg width="10" height="13" viewBox="0 0 16 16" fill="none">
                    <path d="M8 2v8M5 5l3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M3 11v1.5A1.5 1.5 0 004.5 14h7a1.5 1.5 0 001.5-1.5V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  Upload Bank Response
                </button>
              )}
{canDownloadBankResponse && (
  <button
    onClick={() => openConfirm('downloadSendToBank', { 
      id: file.id, 
      fileName: file.fileName, 
      status: file.fileStatus,
      payout_type: file.payoutType  
    })}
    disabled={loading}
    className="w-[160px] flex items-center justify-center gap-1.5 py-1 text-[11px] font-semibold bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
  >
    {file.fileStatus === "downloaded_by_accounts" ? (
      <svg width="10" height="13" viewBox="0 0 16 16" fill="none">
        <path d="M8 14V6M5 11l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 5V3.5A1.5 1.5 0 014.5 2h7A1.5 1.5 0 0113 3.5V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ) : (
      <img src={sendtobankicon} alt="Send to Bank" className="w-[10px] h-[13px]" />
    )}
    {file.fileStatus === "downloaded_by_accounts" ? "Download Again" : "Send to Bank"}
  </button>
)}
            </>
          )}
          {file.fileStatus === "bank_response_uploaded" && canDownloadBankResponse && (
            <>
              <button
                onClick={() => { setSelectedBankFileId(file.id); setSelectedBankFileName(file.fileName); setShowBankModal(true); }}
                disabled={loading}
                className="w-[160px] flex items-center justify-center gap-1.5 py-1 text-[11px] font-semibold text-blue-700 bg-transparent border border-dashed border-purple-300 rounded hover:bg-purple-50 disabled:opacity-50 transition-colors"
              >
                <svg width="10" height="13" viewBox="0 0 16 16" fill="none">
                  <path d="M8 10V2M5 5l3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M3 11v1.5A1.5 1.5 0 004.5 14h7a1.5 1.5 0 001.5-1.5V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                Upload Bank Response
              </button>

              <button
                onClick={() => handleDownloadBankResponse(file.responseS3Url, file.fileName)}
                disabled={!file.responseS3Url}
                className="w-[160px] flex items-center justify-center bg-[#567EBE2B] gap-1 py-1 text-[10px] font-medium text-indigo-600 border border-indigo-300 rounded hover:bg-indigo-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <img src={documentIcon} alt="Document icon" className="w-3 h-3" />
                View Bank Response
              </button>
            </>
          )}
        </>
      )}
    </div>
  </td>
);

                    const rows = [];
                    topLevel.forEach((file, idx) => {
                      const hasChildren = isAccountsOrSuperadmin && childrenByParent[file.id]?.length > 0;
                      const isExpanded = expandedParents.has(file.id);

                      rows.push(
                        <tr
                          key={file.id}
                          className={`border-b transition-colors ${hasChildren ? "hover:brightness-95" : "bg-white hover:bg-gray-50"}`}
                          style={hasChildren ? { backgroundColor: '#F3FCFE' } : {}}
                        >
                          {/* Expand / row number */}
{/* Expand / row number */}
<td className="p-2 text-center">
  <span className="text-xs text-gray-500">
    {(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}.
  </span>
</td>

                          {renderFileNameCell(file, false, hasChildren, isExpanded, () => toggleParentExpand(file.id))}
<td className="p-2 text-center">
  <span className="text-gray-700 text-xs font-medium">
    {file.payoutType && file.payoutType !== "daily_payout"
      ? file.payoutType === "referral"
        ? "Referral"
        : file.payoutType === "incentive"
          ? "Incentive"
          : file.payoutType
      : "Daily Payout"}
  </span>
</td>
                          <td className="p-2 text-center">
<span className="text-gray-700 text-xs font-medium">
  {file.companyName && file.companyName !== "N/A"
    ? `${file.companyName} (${file.companyCode})`
    : "-"}
</span>
                          </td>
                          <td className="p-2 text-center">
                            <span className="text-gray-700 text-xs font-medium">{file.createdBy}</span>
                          </td>
                          <td className="p-2 text-center">

                            <span className="text-gray-800 font-medium text-xs">
{file.dpSuccessCount !== 0 && file.dpSuccessCount != null && (
  <>
    <span className="text-green-600 font-medium">
      {file.dpSuccessCount}
    </span>
    <span className="text-gray-800 font-medium">/</span>
  </>
)}
                              
                               {file.dpTotalCount===0?file.totalRecords:file.dpTotalCount}</span>
                          </td>
                          <td className="p-2 text-center">
                            <span className="text-gray-800 font-medium text-xs">
                              {file.dpSuccessAmount !== 0 && file.dpSuccessAmount != null && (
                                <>
                                <span className="text-green-600 font-medium">{file.dpSuccessAmount}</span>
                                <span className="text-gray-800 font-medium">/</span>
                                </>
                              )}
                              {file.dpTotalAmount===0?file.totalNetAmount:file.dpTotalAmount}
                            </span>
                            
                          </td>
                          <td className="p-2 text-center">
                            <span className="inline-flex items-center gap-1.5" title={file.rejectionReasonByAccounts || file.rejectionReasonByAdmin || ""}>
                              <span className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></span>
                              <span className="text-gray-700 text-xs font-medium">
  {STATUS_LABELS[file.parentFileStatus] || 
   STATUS_LABELS[file.fileStatus] || 
   file.fileStatus}
</span>
                            </span>
                          </td>
                          <td className="p-2 text-center">
                            <button onClick={() => handleViewEditHistory(file.id)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-indigo-600">
                              <img src={edithistoryicon} className="h-3 w-3" alt="edit history" />
                              View
                            </button>
                          </td>

                      {renderActionsCell(file, hasChildren)}
                      
<td className="p-2 text-center">
  {hasChildren && file.fileStatus === "approved_by_accounts" && (
    <img
      src={unlinkicon}
      alt="Unmerge"
      title="Unmerge files"
      onClick={() => openConfirm('unlink', { id: file.id })}
      className="mx-auto cursor-pointer opacity-70 hover:opacity-100 transition-opacity"
      style={{ width: '16px', height: '16px', minWidth: '16px', minHeight: '16px' }}
    />
  )}
</td>
                        </tr>
                      );

                      if (hasChildren && isExpanded) {
                        childrenByParent[file.id].forEach((child) => {
                          rows.push(
<tr key={child.id} className="border-b bg-blue-50/40 hover:bg-blue-50 transition-colors">
  <td className="p-2 text-center">
    <span className="text-xs text-cyan-500 pl-2">↳</span>
  </td>
  {renderFileNameCell(child, true)}
<td className="p-2 text-center">
  <span className="text-gray-500 text-xs italic">
    {child.payoutType && child.payoutType !== "daily_payout"
      ? child.payoutType === "referral"
        ? "Referral"
        : child.payoutType === "incentive"
          ? "Incentive"
          : child.payoutType
      : "Daily Payout"}
  </span>
</td>
  <td className="p-2 text-center">
    <span className="text-gray-500 text-xs italic">
      {child.companyName && child.companyName.toUpperCase() !== "N/A"
        ? `${child.companyName}(${child.companyCode})`
        : "-"}
    </span>
  </td>
  <td className="p-2 text-center">
    <span className="text-gray-500 text-xs italic">{child.createdBy}</span>
  </td>
  <td className="p-2 text-center">
    <span className="text-gray-600 font-medium text-xs">
      {child.dpSuccessCount !== 0 && child.dpSuccessCount != null && (
  <>
    <span className="text-green-600 font-medium">
      {child.dpSuccessCount}
    </span>
    <span className="text-gray-800 font-medium">/</span>
  </>
)}
      {child.totalRecords}</span>
  </td>
  <td className="p-2 text-center">
    <span className="text-gray-600 font-medium text-xs">
     {child.dpSuccessAmount !== 0 && child.dpSuccessAmount != null && (
                                <>
                                <span className="text-green-600 font-medium">{child.dpSuccessAmount}</span>
                                <span className="text-gray-800 font-medium">/</span>
                                </>
                              )}
      {child.totalNetAmount}
    </span>
  </td>
  <td className="p-2 text-center">
    <span className="inline-flex items-center gap-1.5" title={child.rejectionReasonByAccounts || child.rejectionReasonByAdmin || ""}>
      <span className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0"></span>
      <span className="text-gray-500 text-xs italic">{STATUS_LABELS[child.fileStatus] || child.fileStatus}</span>
    </span>
  </td>
  <td className="p-2 text-center">
    <button onClick={() => handleViewEditHistory(child.id)}
      className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium text-indigo-400">
      <img src={edithistoryicon} className="h-3 w-3 opacity-60" alt="edit history" />
      View
    </button>
  </td>
  <td className="p-2"></td>
  <td className="p-2"></td>
</tr>
                          );
                        });
                      }
                    });

                    return rows;
                  })()
                )}
              </tbody>
            </table>
          </div>

          {/* ── Edit History Modal ── */}
          {editmodalopen && (
            <EditHistoryView
              edithistory={editHistoryData}
              loading={editHistoryLoading}
              seteditmodalopen={seteditmodalopen}
              editmodalopen={editmodalopen}
            />
          )}

                    {payouthistorymodal && (
            <Payouthistoryview
              PayoutAccountdata={payoutaccountdata}
              loading={payoutHistoryLoading}
              setpayouthistorymodal={setpayouthistorymodal}
              payouthistorymodal={payouthistorymodal}
            />
          )}

          {/* ── Generate Success Modal ── */}
          {showSuccessModal && (
            <SuccessModal
              onClose={() => setShowSuccessModal(false)}
              records={payoutDetails.records}
              amount={payoutDetails.amount}
              createdBy={payoutDetails.createdBy}
              filename={payoutDetails.filename}
            />
          )}

          {/* ── Merge Success Modal ── */}
          {showMergeSuccessModal && (
            <MergeSuccessModal
              onClose={() => setShowMergeSuccessModal(false)}
              mergeResult={mergeResult}
            />
          )}

          {showBankModal && (
            <UploadBankResponseModal
              fileId={selectedBankFileId}
              filename={selectedBankFileName}
              file_type={selectedValue}
              onClose={() => { setShowBankModal(false); setSelectedBankFileId(null);
                setSelectedBankFileName(null); 
               }}
              onSuccess={() => fetchListingData(currentPage)}
            />
          )}

          {showGenerateConfirmModal && (
            <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40">
              <div className="bg-white rounded-xl shadow-xl w-[320px] p-6 flex flex-col items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center">
                  <svg className="w-7 h-7 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h2 className="text-sm font-bold text-gray-800 text-center">Generate Payout File?</h2>
                <p className="text-xs text-gray-500 text-center leading-relaxed">
                  Are you sure you want to generate the payout file for{' '}
                  <span className="font-semibold text-gray-700">{selectedCompanyName} {selectedLocation}</span> ?
                </p>
                <div className="flex gap-3 w-full mt-1">
                  <button
                    onClick={() => setShowGenerateConfirmModal(false)}
                    className="flex-1 py-1.5 text-xs border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => { setShowGenerateConfirmModal(false); handleGeneratePayoutFile(); }}
                    disabled={generateLoading}
                    className="flex-1 py-1.5 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium transition-colors"
                  >
                    {generateLoading ? spinner : "Yes, Generate"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showApprovalSuccessModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
              <div className="bg-white rounded-xl shadow-xl p-8 flex flex-col items-center gap-4 min-w-[300px]">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                  <svg className="w-9 h-9 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-800">Sent for Approval!</h2>
                <p className="text-sm text-gray-500 text-center">The payout file has been successfully submitted for approval.</p>
                <button
                  onClick={() => { setShowApprovalSuccessModal(false); fetchListingData(currentPage); }}
                  className="px-6 py-2 bg-blue-600 text-white text-sm font-semibold rounded-md hover:bg-blue-700 transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          )}


          {/* ── Approve Payout ── */}
<ConfirmModal
  isOpen={activeConfirm === 'approve'}
  onClose={closeConfirm}
  loading={loading}
  icon={<ApproveIcon />}
  iconBg="bg-green-50"
  title="Approve Payout File?"
  description="Are you sure you want to approve this payout file? This action cannot be undone."
  confirmLabel="Yes, Approve"
  confirmClass="bg-green-600 hover:bg-green-700"
  onConfirm={() => {
    closeConfirm();
    handleSendForaccountApproval(pendingConfirmPayload.id, pendingConfirmPayload.status, true);
  }}
/>

<ConfirmModal
  isOpen={activeConfirm === 'unlink'}
  onClose={closeConfirm}
  loading={loading}
  icon={<img src={unlinkicon} className="w-5 h-5" />}
  iconBg="bg-orange-50"
  title="Unmerge Payout File?"
  description="Are you sure you want to unmerge this payout file? This action cannot be undone."
  confirmLabel="Yes, Unmerge"
  confirmClass="bg-orange-600 hover:bg-orange-700"
  onConfirm={() => {
    closeConfirm();
    handleUnlinkPayoutFile(pendingConfirmPayload.id);
  }}
/>

{/* ── Download / Send to Bank ── */}
<ConfirmModal
  isOpen={activeConfirm === 'downloadSendToBank'}
  onClose={closeConfirm}
  loading={loading}
  icon={<ApproveIcon />}
  iconBg="bg-blue-50"
  title={pendingConfirmPayload?.status === "downloaded_by_accounts" ? "Download Again?" : "Send to Bank?"}
  description={
    pendingConfirmPayload?.status === "downloaded_by_accounts"
      ? "Are you sure you want to download this payout file again?"
      : "Are you sure you want to send this payout file to the bank?"
  }
  confirmLabel={pendingConfirmPayload?.status === "downloaded_by_accounts" ? "Yes, Download" : "Yes, Send"}
  confirmClass="bg-blue-600 hover:bg-blue-700"
  onConfirm={() => {
    closeConfirm();
    if (pendingConfirmPayload.payout_type == "daily_payout") {
      downloadbankresponse(pendingConfirmPayload.id, pendingConfirmPayload.fileName);
    }
    else if(pendingConfirmPayload.payout_type == "incentive")
    {
      downloadincentivebankresponse(pendingConfirmPayload.id, pendingConfirmPayload.fileName);

    }
    
    else {
      downloadreferralbankresponse(pendingConfirmPayload.id, pendingConfirmPayload.fileName);
    }
  }}
/>

{/* ── Generate Payout File ── */}
<ConfirmModal
  isOpen={activeConfirm === 'generatePayout'}
  onClose={closeConfirm}
  loading={generateLoading}
  icon={<FileIcon />}
  iconBg="bg-indigo-50"
  title="Generate Payout File?"
  description={
    <>
      Are you sure you want to generate the payout file for{' '}
      <span className="font-semibold text-gray-700">{selectedCompanyName} {selectedLocation}</span>?
    </>
  }
  confirmLabel="Yes, Generate"
  confirmClass="bg-indigo-600 hover:bg-indigo-700"
  onConfirm={() => { closeConfirm(); handleGeneratePayoutFile(); }}
/>

{/* ── Merge Files ── */}
<ConfirmModal
  isOpen={activeConfirm === 'mergeFiles'}
  onClose={closeConfirm}
  loading={mergeLoading}
  icon={<ApproveIcon />}
  iconBg="bg-purple-50"
  title="Merge Payout Files?"
  description={
    <>
      Are you sure you want to merge{' '}
      <span className="font-semibold text-gray-700">{modalSelectedIds.length} selected files</span> into one combined payout file?
    </>
  }
  confirmLabel="Yes, Merge"
  confirmClass="bg-indigo-600 hover:bg-indigo-700"
  onConfirm={() => { closeConfirm(); handleModalMerge(); }}
/>

          {showapprovalrejectmodal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
              <div className="bg-white rounded-xl shadow-xl p-8 flex flex-col items-center gap-4 min-w-[300px]">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-9 h-9 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h2 className="text-lg font-bold text-gray-800">Rejected</h2>
                <p className="text-sm text-gray-500 text-center">The payout file has been rejected.</p>
                <button
                  onClick={() => { setshowapprovalrejectmodal(false); fetchListingData(currentPage); }}
                  className="px-6 py-2 bg-red-600 text-white text-sm font-semibold rounded-md hover:bg-red-700 transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          )}

          {showRemarksModal && (
            <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40">
              <div className="bg-white rounded-xl shadow-xl w-[360px] p-6 flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-bold text-gray-800">Add Remarks</h2>
                  <button
                    onClick={() => { setShowRemarksModal(false); setRemarks(""); }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 -mt-2">Please provide a reason for rejecting this payout file.</p>
                <textarea
                  rows={4}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Enter remarks..."
                  className="w-full border border-gray-300 rounded-md p-2.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-red-400 focus:border-red-400"
                />
                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowRemarksModal(false); setRemarks(""); }}
                    className="flex-1 py-1.5 text-xs border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRemarksSubmit}
                    disabled={!remarks.trim()}
                    className="flex-1 py-1.5 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed font-medium transition-colors"
                  >
                    Submit & Reject
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Bottom Pagination ── */}
          {showTable && (
            <div className="bg-white border-t border-gray-200 flex items-center pr-4">
              <div className="flex-1">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  itemsPerPage={ITEMS_PER_PAGE}
                  totalItems={totalItems}
                  activeColor="#4f46e5"
                  inactiveColor="#f1f5f9"
                  textColor="#4f46e5"
                  containerStyle={{ borderTop: 'none', padding: '10px 0' }}
                />
              </div>
            </div>
          )}

        </div>

        {/* ── Create Merge Payout File Modal ── */}
        {showMergeModal && (
          <div className="fixed inset-0 z-[250] flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-xl shadow-2xl w-[580px] max-h-[90vh] flex flex-col">

              {/* Modal Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <h2 className="text-sm font-bold text-blue-800">Create a Merge Payout File List</h2>
                <button
                  onClick={() => { setShowMergeModal(false); setModalSelectedIds([]); setModalFiles([]); }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Merge Button + Filters */}
              <div className="px-5 py-3 border-b border-gray-100 flex flex-col gap-2.5">

                {/* Merge Selected Button */}
                <button
                  onClick={() => openConfirm('mergeFiles')}
                  disabled={mergeLoading || modalSelectedIds.length < 2}
                  title={modalSelectedIds.length < 2 ? "Select at least 2 files to merge" : ""}
                  className="self-start px-4 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-md hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                >
                  {mergeLoading ? (
                    <><Loader2 className="h-3 w-3 animate-spin" /> Merging...</>
                  ) : (
                    <span className="flex items-center gap-1.5">
                      <span>&gt;&gt;</span>
                      Merge Selected files
                      {modalSelectedIds.length > 0 && (
                        <span className="bg-white text-indigo-600 rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none">
                          {modalSelectedIds.length}
                        </span>
                      )}
                    </span>
                  )}
                </button>

                {/* Filter Row */}
                <div className="flex items-end gap-2">
                  {/* Date Range */}
                  {/* <div className="flex-1">
                    <label className="text-[10px] text-gray-500 mb-0.5 block">Date Range</label>
                    <DateRangePicker
                      startDate={modalDateRange.startDate}
                      endDate={modalDateRange.endDate}
                      onChange={(range) => setModalDateRange(range)}
                      placeholder="Select dates"
                      height="28px"
                    />
                  </div> */}

                  {/* Status */}
                  {/* <div className="flex-1 relative">
                    <label className="text-[10px] text-gray-500 mb-0.5 block">Status</label>
                    <button
                      onClick={() => setIsModalStatusOpen(!isModalStatusOpen)}
                      className="w-full px-2 h-7 text-xs border border-gray-300 rounded text-left flex justify-between items-center bg-white hover:border-indigo-400 transition-colors"
                    >
                      <span className={modalStatus ? "text-gray-800 truncate" : "text-gray-400"}>
                        {modalStatus ? STATUS_LABELS[modalStatus] : "All Status"}
                      </span>
                      <ChevronIcon open={isModalStatusOpen} />
                    </button>
                    {isModalStatusOpen && (
                      <div className="absolute z-[99999] mt-1 w-full bg-white shadow-lg rounded py-0.5 max-h-36 overflow-auto border border-gray-300">
                        <button
                          onClick={() => { setModalStatus(""); setIsModalStatusOpen(false); }}
                          className={`block w-full text-left px-3 py-1 text-xs hover:bg-indigo-100 ${!modalStatus ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}
                        >
                          All Status
                        </button>
                        {STATUS_OPTIONS.filter(s => s !== "ALL" && !superadminaccess.includes(s)).map((s) => (
                          <button key={s}
                            onClick={() => { setModalStatus(s); setIsModalStatusOpen(false); }}
                            className={`block w-full text-left px-3 py-1 text-xs hover:bg-indigo-100 ${modalStatus === s ? "bg-indigo-50 text-indigo-700" : "text-gray-700"}`}>
                            {STATUS_LABELS[s]}
                          </button>
                        ))}
                      </div>
                    )}
                  </div> */}

                  {/* Apply */}
                  {/* <button
                    onClick={() => fetchModalFiles(1)}
                    disabled={modalLoading}
                    className="px-3 h-7 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 disabled:bg-indigo-300 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
                  >
                    {modalLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : "Apply"}
                  </button> */}
                </div>
              </div>

              {/* Table */}
              <div className="overflow-auto max-h-[50vh]">
                <table className="w-full text-xs border-collapse">
                  <thead className="sticky top-0 bg-gray-50 z-10 shadow-sm">
                    <tr>
                      <th className="p-3 w-10 text-center">
                        <input
                          type="checkbox"
                          checked={modalFiles.length > 0 && modalFiles.every(f => modalSelectedIds.includes(f.id))}
                          onChange={() => {
                            const allIds = modalFiles.map(f => f.id);
                            const allSelected = allIds.every(id => modalSelectedIds.includes(id));
                            setModalSelectedIds(allSelected ? [] : [...new Set([...modalSelectedIds, ...allIds])]);
                          }}
                          className="cursor-pointer accent-indigo-600"
                        />
                      </th>
                      <th className="p-3 text-left font-semibold text-gray-700">File Name</th>
                      <th className="p-3 text-center font-semibold text-gray-700">Company Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalLoading ? (
                      <tr>
                        <td colSpan="3" className="text-center p-10">
                          <Loader2 className="h-5 w-5 animate-spin mx-auto text-indigo-600" />
                        </td>
                      </tr>
                    ) : modalFiles.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="text-center p-10 text-gray-400 text-sm">
                          No files found
                        </td>
                      </tr>
                    ) : (
                      modalFiles.map((file) => (
                        <tr
                          key={file.id}
                          className={`border-b transition-colors cursor-pointer ${modalSelectedIds.includes(file.id) ? "bg-indigo-50" : "hover:bg-gray-50"}`}
                          onClick={() => {
                            setModalSelectedIds(prev =>
                              prev.includes(file.id) ? prev.filter(x => x !== file.id) : [...prev, file.id]
                            );
                          }}
                        >
                          <td className="p-3 text-center" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={modalSelectedIds.includes(file.id)}
                              onChange={() => {
                                setModalSelectedIds(prev =>
                                  prev.includes(file.id) ? prev.filter(x => x !== file.id) : [...prev, file.id]
                                );
                              }}
                              className="cursor-pointer accent-indigo-600"
                            />
                          </td>
                          <td className="p-3">
                            <div className="font-medium text-gray-800 truncate max-w-[240px]" title={file.fileName}>
                              {file.fileName || '-'}
                            </div>
                            <div className="text-[10px] text-gray-400 mt-0.5">
                              {file.createdAt ? new Date(file.createdAt).toLocaleString('en-IN', {
                                day: '2-digit', month: 'short', year: 'numeric',
                                hour: '2-digit', minute: '2-digit', hour12: true
                              }) : '-'}
                            </div>
                          </td>
<td className="p-3 text-center text-gray-700 font-medium">
  {file.companyName && file.companyName.toUpperCase() !== "N/A"
    ? `${file.companyName} (${file.companyCode})`
    : "-"}
</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Modal Pagination */}
              {modalTotalPages > 1 && (
                <div className="border-t border-gray-200 px-4">
                  <Pagination
                    currentPage={modalPage}
                    totalPages={modalTotalPages}
                    onPageChange={(p) => fetchModalFiles(p)}
                    itemsPerPage={MODAL_ITEMS_PER_PAGE}
                    totalItems={modalTotalItems}
                    activeColor="#4f46e5"
                    inactiveColor="#f1f5f9"
                    textColor="#4f46e5"
                    containerStyle={{ borderTop: 'none', padding: '8px 0' }}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        <ErrorModal
          message={errorMessage}
          isOpen={isErrorOpen}
          setIsOpen={setIsErrorOpen}
        />
      </div>
}
    </>
  );
};

export default Payoutlisting;